/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title as ChartTitle,
    Tooltip as ChartTooltip,
    Legend,
    Filler,
} from 'chart.js';
import { getServiceProviderMonthlyComparison } from 'api/rest/analytics';
import { useAuth } from 'contexts/ProtectedRoute';
import { Role } from 'data/constants/roles';
import {
    ProviderComparisonMetricKey,
    ProviderComparisonProvider,
    ServiceProviderMonthlyComparisonData,
} from 'hooks/analytics';
import InfoTooltip from 'components/v2/common/infoTooltip';

import useAnalyticsData from '../../hooks/useAnalyticsData';
import { AnalyticsError, AnalyticsLoading } from '../../components/AnalyticsState';
import {
    SectionCard,
    SectionTitle,
    SectionSubtitle,
    AnalyticsColors,
} from '../../components/analyticsUi';
import type { AnalyticsOutletContext } from '../../AnalyticsShell';
import {
    PROVIDER_COMPARISON,
    PROVIDER_COMPLETED_ASSESSMENTS,
    PROVIDER_FULLY_PAID_APPLICATIONS,
    PROVIDER_SUBMITTED_APPLICATIONS,
    PROVIDER_MONTHLY_AVERAGE,
} from '../../constants/metricDescriptions';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    ChartTitle,
    ChartTooltip,
    Legend,
    Filler
);

const METRICS: { key: ProviderComparisonMetricKey; label: string; tooltip: string }[] = [
    {
        key: 'completedAssessments',
        label: 'Completed Assessments',
        tooltip: PROVIDER_COMPLETED_ASSESSMENTS,
    },
    {
        key: 'fullyPaidApplications',
        label: 'Fully Paid Applications',
        tooltip: PROVIDER_FULLY_PAID_APPLICATIONS,
    },
    {
        key: 'submittedApplications',
        label: 'Submitted Applications',
        tooltip: PROVIDER_SUBMITTED_APPLICATIONS,
    },
];

const PROVIDER_COLOR_BY_NAME: Record<string, string> = {
    'UIS Australia': '#2563EB',
    'UIS Canada': '#DC2626',
    'UIS UK': '#EA580C',
};

const getDefaultHiddenProviderIds = (providers: ProviderComparisonProvider[]): Set<number> => {
    const hidden = new Set<number>();
    providers.forEach((provider) => {
        const hasActivity = METRICS.some(({ key }) => provider.totals[key] > 0);
        if (!hasActivity) {
            hidden.add(provider.id);
        }
    });
    return hidden;
};

const resolveProviderColor = (name: string, fallbackIndex: number, total: number): string => {
    const exact = PROVIDER_COLOR_BY_NAME[name];
    if (exact) return exact;

    const caseInsensitive = Object.entries(PROVIDER_COLOR_BY_NAME).find(
        ([key]) => key.toLowerCase() === name.toLowerCase()
    );
    if (caseInsensitive) return caseInsensitive[1];

    return providerColor(fallbackIndex, total);
};

// Evenly spaced hues so any number of providers gets a distinct, stable color.
const providerColor = (index: number, total: number): string => {
    const hue = Math.round((index * 360) / Math.max(total, 1));
    return `hsl(${hue}, 62%, 48%)`;
};

const buildChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'nearest' as const, intersect: true },
    plugins: {
        legend: { display: false },
        tooltip: {
            backgroundColor: '#1F2937',
            titleFont: { size: 12, weight: '600' as const },
            bodyFont: { size: 13 },
            padding: 10,
            cornerRadius: 8,
            callbacks: {
                label: (ctx: any) => ` ${ctx.dataset.label}: ${ctx.raw}`,
            },
        },
    },
    scales: {
        y: {
            beginAtZero: true,
            ticks: {
                precision: 0,
                font: { size: 11 },
                color: '#9CA3AF',
            },
            grid: { color: '#F3F4F6' },
            border: { display: false },
        },
        x: {
            grid: { display: false },
            ticks: {
                maxRotation: 0,
                font: { size: 11 },
                color: '#6B7280',
            },
            border: { display: false },
        },
    },
});

interface MetricChartProps {
    title: string;
    tooltip: string;
    metricKey: ProviderComparisonMetricKey;
    labels: string[];
    monthKeys: string[];
    visibleProviders: ProviderComparisonProvider[];
    colorById: Record<number, string>;
}

const MetricChart = ({
    title,
    tooltip,
    metricKey,
    labels,
    monthKeys,
    visibleProviders,
    colorById,
}: MetricChartProps) => {
    const chartData = useMemo(
        () => ({
            labels,
            datasets: visibleProviders.map((provider) => {
                const color = colorById[provider.id];
                return {
                    label: provider.name,
                    data: monthKeys.map((key) => {
                        const entry = provider.monthly.find((m) => m.month === key);
                        return entry ? entry[metricKey] : 0;
                    }),
                    borderColor: color,
                    backgroundColor: color,
                    tension: 0.35,
                    pointRadius: 3,
                    pointHoverRadius: 6,
                    borderWidth: 2,
                    fill: false,
                };
            }),
        }),
        [labels, monthKeys, visibleProviders, colorById, metricKey]
    );

    const options = useMemo(buildChartOptions, []);

    return (
        <SectionCard>
            <SectionTitle>{title}</SectionTitle>
            <SectionSubtitle>{tooltip}</SectionSubtitle>
            {visibleProviders.length === 0 ? (
                <Box
                    sx={{
                        textAlign: 'center',
                        p: '3rem',
                        color: AnalyticsColors.textSecondary,
                        fontSize: 14,
                    }}
                >
                    No service providers selected. Enable at least one from the legend above.
                </Box>
            ) : (
                <Box sx={{ height: 320, mt: '0.5rem' }}>
                    <Line data={chartData} options={options as any} />
                </Box>
            )}
        </SectionCard>
    );
};

const ProviderComparisonView = () => {
    const { role } = useAuth();
    const isSuperSuperAdmin = role === Role.SUPER_SUPER_ADMIN;
    const { from, to, serviceProviderId } = useOutletContext<AnalyticsOutletContext>();

    const { data, loading, error } = useAnalyticsData<ServiceProviderMonthlyComparisonData>(
        () => getServiceProviderMonthlyComparison(from, to, serviceProviderId),
        [from, to, serviceProviderId]
    );

    const [hiddenProviderIds, setHiddenProviderIds] = useState<Set<number> | null>(null);

    const colorById = useMemo(() => {
        const map: Record<number, string> = {};
        const providers = data?.serviceProviders ?? [];
        providers.forEach((provider, index) => {
            map[provider.id] = resolveProviderColor(provider.name, index, providers.length);
        });
        return map;
    }, [data]);

    if (!isSuperSuperAdmin) {
        return (
            <AnalyticsError
                title="Restricted view"
                message="This view is restricted to super super admins."
            />
        );
    }

    if (loading) {
        return <AnalyticsLoading />;
    }

    if (error || !data) {
        return (
            <AnalyticsError
                message={error || 'Failed to load service provider comparison. Please try again.'}
            />
        );
    }

    const { months, serviceProviders, summary } = data;
    const labels = months.map((m) => m.label);
    const monthKeys = months.map((m) => m.key);

    const effectiveHiddenProviderIds =
        hiddenProviderIds ?? getDefaultHiddenProviderIds(serviceProviders);

    const toggleProvider = (id: number) => {
        setHiddenProviderIds((prev) => {
            const base = prev ?? getDefaultHiddenProviderIds(serviceProviders);
            const next = new Set(base);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const showAllProviders = () => setHiddenProviderIds(new Set());
    const hideAllProviders = () => setHiddenProviderIds(new Set(serviceProviders.map((p) => p.id)));

    const visibleProviders = serviceProviders.filter((p) => !effectiveHiddenProviderIds.has(p.id));

    const summaryMonthlyAverages = METRICS.reduce<Record<ProviderComparisonMetricKey, number>>(
        (acc, { key }) => {
            const total = summary.reduce((sum, entry) => sum + entry[key], 0);
            acc[key] = summary.length > 0 ? total / summary.length : 0;
            return acc;
        },
        { completedAssessments: 0, fullyPaidApplications: 0, submittedApplications: 0 }
    );

    const tooltipByMetric: Record<ProviderComparisonMetricKey, string> = {
        completedAssessments: PROVIDER_COMPLETED_ASSESSMENTS,
        fullyPaidApplications: PROVIDER_FULLY_PAID_APPLICATIONS,
        submittedApplications: PROVIDER_SUBMITTED_APPLICATIONS,
    };

    return (
        <div>
            <Box sx={{ mb: 2 }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                        bgcolor: AnalyticsColors.cardBg,
                        border: `1px solid ${AnalyticsColors.borderLight}`,
                        borderRadius: '10px',
                        overflow: 'hidden',
                    }}
                >
                    {METRICS.map(({ key, label, tooltip }, index) => (
                        <Box
                            key={key}
                            sx={{
                                display: 'flex',
                                alignItems: 'baseline',
                                justifyContent: 'space-between',
                                gap: 1,
                                px: 1.75,
                                py: 1.1,
                                borderRight: {
                                    sm:
                                        index < METRICS.length - 1
                                            ? `1px solid ${AnalyticsColors.borderLight}`
                                            : 'none',
                                },
                                borderBottom: {
                                    xs:
                                        index < METRICS.length - 1
                                            ? `1px solid ${AnalyticsColors.borderLight}`
                                            : 'none',
                                    sm: 'none',
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: AnalyticsColors.textSecondary,
                                    lineHeight: 1.3,
                                }}
                            >
                                {label}
                                <InfoTooltip title={tooltip} />
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 18,
                                    fontWeight: 700,
                                    color: AnalyticsColors.textPrimary,
                                    lineHeight: 1.2,
                                    fontVariantNumeric: 'tabular-nums',
                                }}
                            >
                                {Math.round(summaryMonthlyAverages[key]).toLocaleString()}
                            </Typography>
                        </Box>
                    ))}
                </Box>
                <Typography
                    sx={{
                        mt: 0.75,
                        fontSize: 11,
                        color: AnalyticsColors.textTertiary,
                    }}
                >
                    {PROVIDER_MONTHLY_AVERAGE}
                </Typography>
            </Box>
            <SectionCard>
                <SectionTitle>
                    Service Providers
                    <InfoTooltip title={PROVIDER_COMPARISON} />
                </SectionTitle>
                <SectionSubtitle>
                    Click a provider to show or hide it across the charts below.
                </SectionSubtitle>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '1rem',
                        flexWrap: 'wrap',
                        mb: '1.25rem',
                    }}
                >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {serviceProviders.map((provider) => {
                            const active = !effectiveHiddenProviderIds.has(provider.id);
                            const color = colorById[provider.id];
                            return (
                                <Box
                                    component="button"
                                    key={provider.id}
                                    type="button"
                                    onClick={() => toggleProvider(provider.id)}
                                    aria-pressed={active}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '7px',
                                        p: '5px 11px',
                                        borderRadius: '16px',
                                        border: `1px solid ${active ? AnalyticsColors.borderMedium : AnalyticsColors.borderLight}`,
                                        background: active
                                            ? AnalyticsColors.cardBg
                                            : AnalyticsColors.surfaceBg,
                                        cursor: 'pointer',
                                        fontSize: '12.5px',
                                        fontWeight: 600,
                                        color: active
                                            ? AnalyticsColors.textPrimary
                                            : AnalyticsColors.textTertiary,
                                        textDecoration: active ? 'none' : 'line-through',
                                        transition: 'all 0.15s ease',
                                        '&:hover': { borderColor: color },
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            width: 11,
                                            height: 11,
                                            borderRadius: '50%',
                                            flexShrink: 0,
                                            background: active ? color : 'transparent',
                                            border: `2px solid ${color}`,
                                        }}
                                    />
                                    {provider.name}
                                </Box>
                            );
                        })}
                    </Box>
                    <Box sx={{ display: 'flex', gap: '0.75rem' }}>
                        <Box
                            component="button"
                            type="button"
                            onClick={showAllProviders}
                            disabled={effectiveHiddenProviderIds.size === 0}
                            sx={{
                                background: 'none',
                                border: 'none',
                                p: 0,
                                fontSize: '12.5px',
                                fontWeight: 600,
                                color: AnalyticsColors.primary,
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' },
                                '&:disabled': {
                                    color: AnalyticsColors.textTertiary,
                                    cursor: 'default',
                                    textDecoration: 'none',
                                },
                            }}
                        >
                            Show all
                        </Box>
                        <Box
                            component="button"
                            type="button"
                            onClick={hideAllProviders}
                            disabled={effectiveHiddenProviderIds.size === serviceProviders.length}
                            sx={{
                                background: 'none',
                                border: 'none',
                                p: 0,
                                fontSize: '12.5px',
                                fontWeight: 600,
                                color: AnalyticsColors.primary,
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' },
                                '&:disabled': {
                                    color: AnalyticsColors.textTertiary,
                                    cursor: 'default',
                                    textDecoration: 'none',
                                },
                            }}
                        >
                            Hide all
                        </Box>
                    </Box>
                </Box>
            </SectionCard>
            {METRICS.map(({ key, label }) => (
                <MetricChart
                    key={key}
                    title={label}
                    tooltip={tooltipByMetric[key]}
                    metricKey={key}
                    labels={labels}
                    monthKeys={monthKeys}
                    visibleProviders={visibleProviders}
                    colorById={colorById}
                />
            ))}
        </div>
    );
};

export default ProviderComparisonView;
